import { Dispatch, SetStateAction } from 'react';

interface RegisteredElements {
  [key: string]: {
    el: HTMLElement;
    callback: Dispatch<SetStateAction<boolean>>;
    percentOfViewport: number;
  };
}

class DetectMostVisibleElement {
  /**
   * HTML elements and setState callbacks indexed by element id.
   */
  private elements: RegisteredElements = {};

  /**
   * Debounce timeout for scroll event.
   */
  private timeout: number;

  /**
   * Id of timer from setTimeout.
   */
  private timer: number;

  /**
   * Id of the previously calculated most visible element.
   */
  private previousId: string;

  constructor() {
    // window is unavailable during build process ¯\_(ツ)_/¯
    if (typeof window !== 'undefined') {
      window?.addEventListener('scroll', this.onScroll.bind(this));
    }
    this.timeout = 200;
    this.timer = 0;
    this.previousId = '';
  }

  /**
   * Find what percentage of the viewport that the supplied element occupies.
   * This is determined one-dimensionally, using vertical measurements.
   * @param el HTML element
   * @returns Percent of viewport the element occupies.
   */
  static calculatePercentOfViewport(el: HTMLElement): number {
    const rect = el.getBoundingClientRect();
    const viewportHeight = window?.innerHeight;
    const { height, top, bottom } = rect;
    let percent = 0;

    // if the element is not visible, return 0
    if (top + height <= 0 || top > viewportHeight) {
      percent = 0;
    } else if (top <= 0 && bottom >= viewportHeight) {
      // if the element's top position and bottom position are greater than the viewport, it's filling up the entire visible area. Return 100.
      percent = 100;
    } else {
      // if the element is partially visible, calculate the percent of the viewport it occupies.
      const calculatedBottom =
        bottom > viewportHeight ? viewportHeight : bottom;
      const calculatedTop = top < 0 ? 0 : top;
      percent = ((calculatedBottom - calculatedTop) / viewportHeight) * 100;
    }
    return percent;
  }

  /**
   * Get the id of the element that is most visible.
   * @returns The id of the element that is most visible.
   */
  private getMostVisibleElementId(): string | undefined {
    const highestPercent = Math.max(
      ...Object.values(this.elements).map(
        ({ percentOfViewport }) => percentOfViewport,
      ),
    );
    if (highestPercent === 0) {
      return undefined;
    }
    const winningEntry = Object.entries(this.elements).find(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_id, { percentOfViewport }]) => percentOfViewport === highestPercent,
    );
    return winningEntry?.[0]; // id is the zero index, html element is the 1 index.
  }

  /**
   * On scroll event, check the percentage of viewport that each element occupies.
   */
  private onScroll(): void {
    if (this.timer) {
      window?.clearTimeout(this.timer);
    }
    this.timer = window?.setTimeout(() => {
      if (this.elements && Object.keys(this.elements).length) {
        this.resetAllElements();
        Object.entries(this.elements).forEach(([id, { el }]) => {
          const percent =
            DetectMostVisibleElement.calculatePercentOfViewport(el);
          this.elements[id].percentOfViewport = percent;
        });
      }
      const winnerId = this.getMostVisibleElementId();
      if (winnerId && winnerId !== this.previousId) {
        this.elements[winnerId].callback(true);
        this.previousId = winnerId;
      }
    }, this.timeout);
  }

  /**
   * Reset all elements callbacks to false. Do this at the beginning of a scroll event so we don't
   * end up with multiple elements set to true.
   */
  private resetAllElements(): void {
    Object.values(this.elements).forEach(({ el, callback }) => {
      this.elements[el.id].percentOfViewport = 0;
      callback(false);
    });
  }

  /**
   * Register an element and its callback.
   * @param el HTML element
   * @param callback SetState callback
   */
  public register(id: string, callback: Dispatch<SetStateAction<boolean>>) {
    const element = document.getElementById(id);
    if (element) {
      if (!this.elements[id]) {
        this.elements[id] = {
          el: element,
          callback,
          percentOfViewport: 0,
        };
      }
    }
  }

  /**
   * Unregister an element.
   * @param id Element id
   */
  public unregister(id: string) {
    delete this.elements[id];
  }
}

// Export the singleton instance.
const instance = new DetectMostVisibleElement();
export default instance;
